import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-hot-toast';
import { 
  Download, 
  Users, 
  DollarSign, 
  TrendingUp, 
  Search,
  Filter,
  Calendar,
  Eye,
  RefreshCw,
  LogOut,
  Trash2,
  AlertTriangle,
  Settings,
  Menu,
  X,
  ChevronLeft,
  ChevronRight,
  Phone,
  Mail,
  Briefcase
} from 'lucide-react';
import { leadService } from '../services/leadService';
import { exportToCSV } from '../utils/csvExport';
import { auth } from '../services/cloudflareAPI';

const Dashboard = () => {
  const navigate = useNavigate();
  const [leads, setLeads] = useState([]);
  const [filteredLeads, setFilteredLeads] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [loanTypeFilter, setLoanTypeFilter] = useState('all');
  const [currentPage, setCurrentPage] = useState(1);
  const [activeTab, setActiveTab] = useState('all');
  const [customDateFrom, setCustomDateFrom] = useState('');
  const [customDateTo, setCustomDateTo] = useState('');
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [selectedLead, setSelectedLead] = useState(null);
  const [refreshing, setRefreshing] = useState(false);
  
  const LEADS_PER_PAGE = 20;

  // Initial fetch and real-time listener
  useEffect(() => {
    const unsubscribe = leadService.onLeadsChange((leadsData) => {
      setLeads(leadsData);
      setLoading(false);
      setRefreshing(false);
    });

    return () => unsubscribe();
  }, []);

  // Manual refresh function
  const handleRefresh = async () => {
    setRefreshing(true);
    try {
      const result = await leadService.getLeads();
      if (result.success) {
        setLeads(result.leads);
      }
    } catch (error) {
      console.error('Refresh error:', error);
    }
    setRefreshing(false);
  };

  useEffect(() => {
    let filtered = leads;

    const today = new Date();
    today.setHours(0, 0, 0, 0);
    
    if (activeTab === 'today') {
      filtered = filtered.filter(lead => {
        if (!lead.createdAt) return false;
        const leadDate = new Date(lead.createdAt);
        leadDate.setHours(0, 0, 0, 0);
        return leadDate.getTime() === today.getTime();
      });
    } else if (activeTab === 'custom' && customDateFrom && customDateTo) {
      const fromDate = new Date(customDateFrom);
      const toDate = new Date(customDateTo);
      fromDate.setHours(0, 0, 0, 0);
      toDate.setHours(23, 59, 59, 999);
      
      filtered = filtered.filter(lead => {
        if (!lead.createdAt) return false;
        const leadDate = new Date(lead.createdAt);
        return leadDate >= fromDate && leadDate <= toDate;
      });
    }

    if (searchTerm) {
      const term = searchTerm.toLowerCase();
      filtered = filtered.filter(lead => 
        lead.firstName?.toLowerCase().includes(term) ||
        lead.lastName?.toLowerCase().includes(term) ||
        lead.email?.toLowerCase().includes(term) ||
        lead.phone?.includes(searchTerm)
      );
    }

    if (statusFilter !== 'all') {
      filtered = filtered.filter(lead => lead.status === statusFilter);
    }

    if (loanTypeFilter !== 'all') {
      filtered = filtered.filter(lead => lead.loanType === loanTypeFilter);
    }

    setFilteredLeads(filtered);
    setCurrentPage(1);
  }, [leads, searchTerm, statusFilter, loanTypeFilter, activeTab, customDateFrom, customDateTo]);

  const handleExportCSV = () => {
    if (filteredLeads.length === 0) {
      toast.error('No leads to export');
      return;
    }
    
    exportToCSV(filteredLeads);
    toast.success(`Exported ${filteredLeads.length} leads to CSV`);
  };

  const handleLogout = async () => {
    try {
      await auth.signOut();
      localStorage.removeItem('adminAuthenticated');
      localStorage.removeItem('adminLoginTime');
      localStorage.removeItem('adminUid');
      toast.success('Logged out successfully');
      navigate('/admin/login');
    } catch (error) {
      console.error('Logout error:', error);
      toast.error('Error logging out');
    }
  };

  // Helper function to count today's leads - must be defined before getStats
  const getTodayLeadsCount = () => {
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    return leads.filter(lead => {
      if (!lead.createdAt) return false;
      const leadDate = new Date(lead.createdAt);
      leadDate.setHours(0, 0, 0, 0);
      return leadDate.getTime() === today.getTime();
    }).length;
  };

  const getStats = () => {
    const totalLeads = leads.length;
    const todayLeads = getTodayLeadsCount();
    const totalLoanAmount = leads.reduce((sum, lead) => {
      if (!lead.loanAmount) return sum;
      if (typeof lead.loanAmount === 'string' && lead.loanAmount.includes('-')) {
        const [min, max] = lead.loanAmount.split('-').map(val => parseFloat(val) || 0);
        return sum + ((min + max) / 2);
      }
      return sum + (parseFloat(lead.loanAmount) || 0);
    }, 0);

    return { totalLeads, todayLeads, totalLoanAmount };
  };

  const stats = getStats();

  const formatCurrency = (amount) => {
    if (!amount || isNaN(amount)) return '$0';
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(amount);
  };

  const formatLoanAmount = (loanAmount) => {
    if (!loanAmount) return 'N/A';
    if (typeof loanAmount === 'string' && loanAmount.includes('-')) {
      const [min, max] = loanAmount.split('-');
      return `$${parseInt(min).toLocaleString()} - $${parseInt(max).toLocaleString()}`;
    }
    if (!isNaN(loanAmount)) {
      return formatCurrency(loanAmount);
    }
    return loanAmount;
  };

  const formatIncome = (income) => {
    if (!income) return 'N/A';
    if (typeof income === 'string' && income.includes('-')) {
      const [min, max] = income.split('-');
      return `$${parseInt(min).toLocaleString()} - $${parseInt(max).toLocaleString()}`;
    }
    if (income === '5000+') return '$5,000+';
    if (!isNaN(income)) {
      return formatCurrency(income);
    }
    return income;
  };

  const formatDate = (date) => {
    if (!date) return 'N/A';
    return new Date(date).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const formatCreditScore = (score) => {
    const scoreMap = {
      'excellent': 'Excellent (720+)',
      'good': 'Good (680-719)',
      'fair': 'Fair (640-679)',
      'poor': 'Poor (580-639)',
      'bad': 'Bad (<580)',
      'not-sure': 'Unknown'
    };
    return scoreMap[score] || score || 'N/A';
  };

  const formatEmployment = (type) => {
    const typeMap = {
      'employed': 'Full-Time',
      'part-time': 'Part-Time',
      'self-employed': 'Self-Employed',
      'benefits': 'Benefits/SSI',
      'retired': 'Retired',
      'other': 'Other'
    };
    return typeMap[type] || type || 'N/A';
  };

  const getUniqueValues = (key) => {
    return [...new Set(leads.map(lead => lead[key]).filter(Boolean))];
  };

  const totalPages = Math.ceil(filteredLeads.length / LEADS_PER_PAGE);
  const startIndex = (currentPage - 1) * LEADS_PER_PAGE;
  const endIndex = startIndex + LEADS_PER_PAGE;
  const currentLeads = filteredLeads.slice(startIndex, endIndex);

  const handlePageChange = (page) => {
    setCurrentPage(page);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const clearFilters = () => {
    setSearchTerm('');
    setStatusFilter('all');
    setLoanTypeFilter('all');
    setActiveTab('all');
    setCustomDateFrom('');
    setCustomDateTo('');
    setCurrentPage(1);
  };

  const handleDeleteLead = async (leadId) => {
    if (window.confirm('Delete this lead? This cannot be undone.')) {
      try {
        await leadService.deleteLead(leadId);
        toast.success('Lead deleted');
        setSelectedLead(null);
      } catch (error) {
        toast.error('Failed to delete lead');
      }
    }
  };

  const handleDeleteAllLeads = async () => {
    if (window.confirm('⚠️ DELETE ALL LEADS? This cannot be undone!')) {
      if (window.confirm('Final confirmation - delete everything?')) {
        try {
          await leadService.deleteAllLeads();
          toast.success('All leads deleted');
        } catch (error) {
          toast.error('Failed to delete leads');
        }
      }
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-900 flex items-center justify-center">
        <div className="text-center">
          <RefreshCw className="h-12 w-12 text-green-500 animate-spin mx-auto mb-4" />
          <p className="text-xl text-white">Loading dashboard...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-900">
      {/* Mobile Header */}
      <div className="lg:hidden bg-slate-800 border-b border-slate-700 sticky top-0 z-40">
        <div className="flex items-center justify-between px-4 py-3">
          <h1 className="text-lg font-bold text-white">Dashboard</h1>
          <div className="flex items-center gap-2">
            <button
              onClick={handleRefresh}
              className={`p-2 text-slate-400 hover:text-white ${refreshing ? 'animate-spin' : ''}`}
            >
              <RefreshCw className="h-5 w-5" />
            </button>
            <button
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
              className="p-2 text-slate-400 hover:text-white"
            >
              {mobileMenuOpen ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
            </button>
          </div>
        </div>
        
        {/* Mobile Menu */}
        {mobileMenuOpen && (
          <div className="bg-slate-800 border-t border-slate-700 p-4 space-y-2">
            <a href="/" className="block w-full text-left px-4 py-3 bg-blue-600 text-white rounded-lg font-medium">
              View Form
            </a>
            <button onClick={handleExportCSV} className="w-full text-left px-4 py-3 bg-green-600 text-white rounded-lg font-medium flex items-center gap-2">
              <Download className="h-4 w-4" /> Export CSV
            </button>
            <button onClick={() => navigate('/admin/settings')} className="w-full text-left px-4 py-3 bg-slate-700 text-white rounded-lg font-medium flex items-center gap-2">
              <Settings className="h-4 w-4" /> Settings
            </button>
            <button onClick={handleLogout} className="w-full text-left px-4 py-3 bg-red-600 text-white rounded-lg font-medium flex items-center gap-2">
              <LogOut className="h-4 w-4" /> Logout
            </button>
          </div>
        )}
      </div>

      {/* Desktop Header */}
      <div className="hidden lg:block bg-slate-800 border-b border-slate-700">
        <div className="max-w-7xl mx-auto px-6 py-4">
          <div className="flex items-center justify-between">
            <div>
              <div className="flex items-center gap-3">
                <h1 className="text-2xl font-bold text-white">Admin Dashboard</h1>
                <span className="bg-red-500/20 text-red-400 text-xs font-medium px-2.5 py-1 rounded-full">ADMIN</span>
              </div>
              <p className="text-slate-400 text-sm mt-1">Manage loan applications</p>
            </div>
            <div className="flex items-center gap-3">
              <button
                onClick={handleRefresh}
                className={`p-2 text-slate-400 hover:text-white transition-colors ${refreshing ? 'animate-spin' : ''}`}
                title="Refresh"
              >
                <RefreshCw className="h-5 w-5" />
              </button>
              <a href="/" className="px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg font-medium transition-colors">
                View Form
              </a>
              <button onClick={handleExportCSV} className="px-4 py-2 bg-green-600 hover:bg-green-700 text-white rounded-lg font-medium flex items-center gap-2 transition-colors">
                <Download className="h-4 w-4" /> Export
              </button>
              <button onClick={() => navigate('/admin/settings')} className="px-4 py-2 bg-slate-700 hover:bg-slate-600 text-white rounded-lg font-medium flex items-center gap-2 transition-colors">
                <Settings className="h-4 w-4" /> Settings
              </button>
              <button onClick={handleLogout} className="px-4 py-2 bg-red-600 hover:bg-red-700 text-white rounded-lg font-medium flex items-center gap-2 transition-colors">
                <LogOut className="h-4 w-4" /> Logout
              </button>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 lg:px-6 py-4 lg:py-6">
        {/* Stats Cards - Responsive Grid */}
        <div className="grid grid-cols-2 lg:grid-cols-3 gap-3 lg:gap-6 mb-4 lg:mb-6">
          <div className="bg-slate-800 rounded-xl p-4 lg:p-6 border border-slate-700">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-slate-400 text-xs lg:text-sm font-medium">Total Leads</p>
                <p className="text-2xl lg:text-3xl font-bold text-white">{stats.totalLeads}</p>
              </div>
              <div className="bg-blue-500/20 p-2 lg:p-3 rounded-full">
                <Users className="h-5 w-5 lg:h-6 lg:w-6 text-blue-400" />
              </div>
            </div>
          </div>

          <div className="bg-slate-800 rounded-xl p-4 lg:p-6 border border-slate-700">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-slate-400 text-xs lg:text-sm font-medium">Today</p>
                <p className="text-2xl lg:text-3xl font-bold text-white">{stats.todayLeads}</p>
              </div>
              <div className="bg-green-500/20 p-2 lg:p-3 rounded-full">
                <TrendingUp className="h-5 w-5 lg:h-6 lg:w-6 text-green-400" />
              </div>
            </div>
          </div>

          <div className="col-span-2 lg:col-span-1 bg-slate-800 rounded-xl p-4 lg:p-6 border border-slate-700">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-slate-400 text-xs lg:text-sm font-medium">Est. Loan Value</p>
                <p className="text-2xl lg:text-3xl font-bold text-white">{formatCurrency(stats.totalLoanAmount)}</p>
              </div>
              <div className="bg-emerald-500/20 p-2 lg:p-3 rounded-full">
                <DollarSign className="h-5 w-5 lg:h-6 lg:w-6 text-emerald-400" />
              </div>
            </div>
          </div>
        </div>

        {/* Date Tabs - Responsive */}
        <div className="bg-slate-800 rounded-xl p-4 lg:p-6 mb-4 lg:mb-6 border border-slate-700">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 mb-4">
            <h3 className="text-white font-semibold">Filter by Date</h3>
            <div className="flex flex-wrap gap-2">
              {['all', 'today', 'custom'].map((tab) => (
                <button
                  key={tab}
                  onClick={() => setActiveTab(tab)}
                  className={`px-3 py-1.5 rounded-lg text-sm font-medium transition-colors ${
                    activeTab === tab
                      ? 'bg-green-600 text-white'
                      : 'bg-slate-700 text-slate-300 hover:bg-slate-600'
                  }`}
                >
                  {tab === 'all' && `All (${leads.length})`}
                  {tab === 'today' && `Today (${getTodayLeadsCount()})`}
                  {tab === 'custom' && 'Custom'}
                </button>
              ))}
            </div>
          </div>

          {activeTab === 'custom' && (
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mt-4 p-4 bg-slate-700/50 rounded-lg">
              <div>
                <label className="block text-xs text-slate-400 mb-1">From</label>
                <input
                  type="date"
                  value={customDateFrom}
                  onChange={(e) => setCustomDateFrom(e.target.value)}
                  className="w-full px-3 py-2 bg-slate-700 border border-slate-600 rounded-lg text-white focus:ring-2 focus:ring-green-500 focus:border-green-500"
                />
              </div>
              <div>
                <label className="block text-xs text-slate-400 mb-1">To</label>
                <input
                  type="date"
                  value={customDateTo}
                  onChange={(e) => setCustomDateTo(e.target.value)}
                  className="w-full px-3 py-2 bg-slate-700 border border-slate-600 rounded-lg text-white focus:ring-2 focus:ring-green-500 focus:border-green-500"
                />
              </div>
            </div>
          )}
        </div>

        {/* Search & Filters - Responsive */}
        <div className="bg-slate-800 rounded-xl p-4 lg:p-6 mb-4 lg:mb-6 border border-slate-700">
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
            <div>
              <label className="block text-xs text-slate-400 mb-1 flex items-center gap-1">
                <Search className="h-3 w-3" /> Search
              </label>
              <input
                type="text"
                placeholder="Name, email, phone..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full px-3 py-2 bg-slate-700 border border-slate-600 rounded-lg text-white placeholder-slate-400 focus:ring-2 focus:ring-green-500 focus:border-green-500"
              />
            </div>

            <div>
              <label className="block text-xs text-slate-400 mb-1 flex items-center gap-1">
                <Filter className="h-3 w-3" /> Status
              </label>
              <select
                value={statusFilter}
                onChange={(e) => setStatusFilter(e.target.value)}
                className="w-full px-3 py-2 bg-slate-700 border border-slate-600 rounded-lg text-white focus:ring-2 focus:ring-green-500 focus:border-green-500"
              >
                <option value="all">All Status</option>
                <option value="new">New</option>
                <option value="contacted">Contacted</option>
                <option value="approved">Approved</option>
                <option value="rejected">Rejected</option>
              </select>
            </div>

            <div>
              <label className="block text-xs text-slate-400 mb-1">Loan Type</label>
              <select
                value={loanTypeFilter}
                onChange={(e) => setLoanTypeFilter(e.target.value)}
                className="w-full px-3 py-2 bg-slate-700 border border-slate-600 rounded-lg text-white focus:ring-2 focus:ring-green-500 focus:border-green-500"
              >
                <option value="all">All Types</option>
                {getUniqueValues('loanType').map(type => (
                  <option key={type} value={type}>{type}</option>
                ))}
              </select>
            </div>

            <div className="flex items-end">
              <button
                onClick={clearFilters}
                className="w-full px-4 py-2 bg-slate-700 hover:bg-slate-600 text-white rounded-lg font-medium transition-colors"
              >
                Clear Filters
              </button>
            </div>
          </div>
        </div>

        {/* Leads Table/Cards */}
        <div className="bg-slate-800 rounded-xl border border-slate-700 overflow-hidden">
          <div className="px-4 lg:px-6 py-3 lg:py-4 border-b border-slate-700 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2">
            <h2 className="text-white font-semibold flex items-center gap-2">
              <Eye className="h-4 w-4 text-green-400" />
              Leads ({filteredLeads.length})
            </h2>
            <span className="text-slate-400 text-xs lg:text-sm">
              Showing {startIndex + 1}-{Math.min(endIndex, filteredLeads.length)} of {filteredLeads.length}
            </span>
          </div>

          {/* Desktop Table View */}
          <div className="hidden lg:block overflow-x-auto">
            <table className="min-w-full">
              <thead className="bg-slate-700/50">
                <tr>
                  <th className="px-4 py-3 text-left text-xs font-medium text-slate-400 uppercase">Applicant</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-slate-400 uppercase">Contact</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-slate-400 uppercase">Loan</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-slate-400 uppercase">Income</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-slate-400 uppercase">Credit</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-slate-400 uppercase">Date</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-slate-400 uppercase">Status</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-slate-400 uppercase">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-700">
                {currentLeads.length === 0 ? (
                  <tr>
                    <td colSpan="8" className="px-6 py-12 text-center">
                      <Users className="h-12 w-12 mx-auto mb-4 text-slate-600" />
                      <p className="text-slate-400 text-lg">No leads found</p>
                      <p className="text-slate-500 text-sm">Leads will appear here as they are submitted</p>
                    </td>
                  </tr>
                ) : (
                  currentLeads.map((lead) => (
                    <tr key={lead.id} className="hover:bg-slate-700/50 transition-colors">
                      <td className="px-4 py-3">
                        <div className="text-white font-medium">{lead.firstName} {lead.lastName}</div>
                        <div className="text-slate-400 text-xs">DOB: {lead.dateOfBirth || 'N/A'}</div>
                      </td>
                      <td className="px-4 py-3">
                        <div className="text-slate-300 text-sm">{lead.email}</div>
                        <div className="text-slate-400 text-xs">{lead.phone}</div>
                      </td>
                      <td className="px-4 py-3">
                        <div className="text-green-400 font-medium">{formatLoanAmount(lead.loanAmount)}</div>
                        <div className="text-slate-400 text-xs">{lead.loanType || 'Payday'}</div>
                      </td>
                      <td className="px-4 py-3">
                        <div className="text-slate-300 text-sm">{formatIncome(lead.monthlyIncome)}</div>
                        <div className="text-slate-400 text-xs">{formatEmployment(lead.employmentType)}</div>
                      </td>
                      <td className="px-4 py-3">
                        <div className="text-slate-300 text-sm">{formatCreditScore(lead.creditScore)}</div>
                      </td>
                      <td className="px-4 py-3">
                        <div className="text-slate-400 text-sm flex items-center gap-1">
                          <Calendar className="h-3 w-3" />
                          {formatDate(lead.createdAt)}
                        </div>
                      </td>
                      <td className="px-4 py-3">
                        <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
                          lead.status === 'new' ? 'bg-blue-500/20 text-blue-400' :
                          lead.status === 'contacted' ? 'bg-yellow-500/20 text-yellow-400' :
                          lead.status === 'approved' ? 'bg-green-500/20 text-green-400' :
                          'bg-red-500/20 text-red-400'
                        }`}>
                          {lead.status || 'new'}
                        </span>
                      </td>
                      <td className="px-4 py-3">
                        <button
                          onClick={() => handleDeleteLead(lead.id)}
                          className="text-red-400 hover:text-red-300 p-1 transition-colors"
                          title="Delete"
                        >
                          <Trash2 className="h-4 w-4" />
                        </button>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>

          {/* Mobile Card View */}
          <div className="lg:hidden divide-y divide-slate-700">
            {currentLeads.length === 0 ? (
              <div className="px-4 py-12 text-center">
                <Users className="h-12 w-12 mx-auto mb-4 text-slate-600" />
                <p className="text-slate-400">No leads found</p>
              </div>
            ) : (
              currentLeads.map((lead) => (
                <div 
                  key={lead.id} 
                  className="p-4 hover:bg-slate-700/50 transition-colors cursor-pointer"
                  onClick={() => setSelectedLead(selectedLead?.id === lead.id ? null : lead)}
                >
                  <div className="flex items-start justify-between mb-2">
                    <div>
                      <div className="text-white font-medium">{lead.firstName} {lead.lastName}</div>
                      <div className="text-green-400 font-semibold">{formatLoanAmount(lead.loanAmount)}</div>
                    </div>
                    <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
                      lead.status === 'new' ? 'bg-blue-500/20 text-blue-400' :
                      lead.status === 'contacted' ? 'bg-yellow-500/20 text-yellow-400' :
                      lead.status === 'approved' ? 'bg-green-500/20 text-green-400' :
                      'bg-red-500/20 text-red-400'
                    }`}>
                      {lead.status || 'new'}
                    </span>
                  </div>
                  
                  <div className="flex items-center gap-4 text-slate-400 text-xs mb-2">
                    <span className="flex items-center gap-1">
                      <Calendar className="h-3 w-3" />
                      {formatDate(lead.createdAt)}
                    </span>
                    <span>{lead.loanType || 'Payday'}</span>
                  </div>

                  {/* Expanded Details */}
                  {selectedLead?.id === lead.id && (
                    <div className="mt-3 pt-3 border-t border-slate-700 space-y-2 animate-fadeIn">
                      <div className="flex items-center gap-2 text-slate-300 text-sm">
                        <Mail className="h-4 w-4 text-slate-400" />
                        {lead.email}
                      </div>
                      <div className="flex items-center gap-2 text-slate-300 text-sm">
                        <Phone className="h-4 w-4 text-slate-400" />
                        {lead.phone}
                      </div>
                      <div className="flex items-center gap-2 text-slate-300 text-sm">
                        <Briefcase className="h-4 w-4 text-slate-400" />
                        {formatEmployment(lead.employmentType)} • {formatIncome(lead.monthlyIncome)}
                      </div>
                      <div className="grid grid-cols-2 gap-2 text-sm">
                        <div>
                          <span className="text-slate-500">DOB:</span>
                          <span className="text-slate-300 ml-1">{lead.dateOfBirth || 'N/A'}</span>
                        </div>
                        <div>
                          <span className="text-slate-500">Credit:</span>
                          <span className="text-slate-300 ml-1">{formatCreditScore(lead.creditScore)}</span>
                        </div>
                      </div>
                      <button
                        onClick={(e) => { e.stopPropagation(); handleDeleteLead(lead.id); }}
                        className="w-full mt-2 px-4 py-2 bg-red-600/20 text-red-400 rounded-lg font-medium flex items-center justify-center gap-2"
                      >
                        <Trash2 className="h-4 w-4" /> Delete Lead
                      </button>
                    </div>
                  )}
                </div>
              ))
            )}
          </div>

          {/* Pagination */}
          {totalPages > 1 && (
            <div className="px-4 lg:px-6 py-3 border-t border-slate-700 flex items-center justify-between">
              <span className="text-slate-400 text-sm">
                Page {currentPage} of {totalPages}
              </span>
              <div className="flex items-center gap-2">
                <button
                  onClick={() => handlePageChange(currentPage - 1)}
                  disabled={currentPage === 1}
                  className="p-2 bg-slate-700 text-white rounded-lg disabled:opacity-50 disabled:cursor-not-allowed hover:bg-slate-600 transition-colors"
                >
                  <ChevronLeft className="h-4 w-4" />
                </button>
                
                <div className="hidden sm:flex items-center gap-1">
                  {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                    let page;
                    if (totalPages <= 5) page = i + 1;
                    else if (currentPage <= 3) page = i + 1;
                    else if (currentPage >= totalPages - 2) page = totalPages - 4 + i;
                    else page = currentPage - 2 + i;

                    return (
                      <button
                        key={page}
                        onClick={() => handlePageChange(page)}
                        className={`w-8 h-8 rounded-lg text-sm font-medium transition-colors ${
                          currentPage === page
                            ? 'bg-green-600 text-white'
                            : 'bg-slate-700 text-slate-300 hover:bg-slate-600'
                        }`}
                      >
                        {page}
                      </button>
                    );
                  })}
                </div>

                <button
                  onClick={() => handlePageChange(currentPage + 1)}
                  disabled={currentPage === totalPages}
                  className="p-2 bg-slate-700 text-white rounded-lg disabled:opacity-50 disabled:cursor-not-allowed hover:bg-slate-600 transition-colors"
                >
                  <ChevronRight className="h-4 w-4" />
                </button>
              </div>
            </div>
          )}
        </div>

        {/* Floating Delete All Button */}
        {leads.length > 0 && (
          <button
            onClick={handleDeleteAllLeads}
            className="fixed bottom-4 right-4 lg:bottom-6 lg:right-6 bg-red-600 hover:bg-red-700 text-white p-3 lg:p-4 rounded-full shadow-lg transition-all z-50 flex items-center gap-2"
            title="Delete All Leads"
          >
            <AlertTriangle className="h-5 w-5" />
            <Trash2 className="h-5 w-5" />
            <span className="hidden lg:inline font-medium">Delete All</span>
          </button>
        )}
      </div>
    </div>
  );
};

export default Dashboard;
